// kotizuMaskProtocol.js
// 大正古地図マスキングプロトコル
// TMSタイルを取得し、県境GeoJSONポリゴン内のみ描画してマスクする
// URL形式: kotizumask://{maskKey}/{tileHost}/{tilePath}/{z}/{x}/{y}.png
//
// MapLibreには clip レイヤーがないため、
// タイル取得→Canvas上でポリゴンクリップ→マスク済み画像を返す方式で実装

import maskDep from '@/js/mask-dep.json'

// Web Mercator (EPSG:3857) 変換ヘルパー
function lngToTileX(lng, z) {
  return ((lng + 180) / 360) * Math.pow(2, z)
}
function latToTileY(lat, z) {
  const latRad = (lat * Math.PI) / 180
  return ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * Math.pow(2, z)
}

export function registerKotizuMaskProtocol(maplibregl) {
  maplibregl.addProtocol('kotizumask', async (params) => {
    try {
      // URL解析: kotizumask://{maskKey}/{残りのURL（TMS形式のhttps://なし）}
      const url = params.url.replace('kotizumask://', '')
      const firstSlash = url.indexOf('/')
      const maskKey = url.substring(0, firstSlash)
      const tileUrlPath = url.substring(firstSlash + 1)

      // タイル座標を抽出 .../{z}/{x}/{y}.png
      const tileMatch = tileUrlPath.match(/(\d+)\/(\d+)\/(\d+)\.png$/)
      if (!tileMatch) {
        return { data: new Uint8Array(0) }
      }
      const z = parseInt(tileMatch[1], 10)
      const x = parseInt(tileMatch[2], 10)
      const y = parseInt(tileMatch[3], 10)

      // TMS: y座標を反転
      const tmsY = Math.pow(2, z) - 1 - y

      // 実際のタイルURLを構築（{z}/{x}/{y}部分を置換）
      const actualUrl = 'https://' + tileUrlPath
        .replace(/\d+\/\d+\/\d+\.png$/, `${z}/${x}/${tmsY}.png`)

      // マスクデータ取得
      const maskData = maskDep[maskKey]
      if (!maskData) {
        // マスクデータがなければそのまま返す
        const resp = await fetch(actualUrl)
        if (!resp.ok) return { data: new Uint8Array(0) }
        const data = new Uint8Array(await resp.arrayBuffer())
        return { data }
      }

      // タイル画像を取得
      const resp = await fetch(actualUrl)
      if (!resp.ok) return { data: new Uint8Array(0) }
      const blob = await resp.blob()
      const imgBitmap = await createImageBitmap(blob)

      // Canvas上でマスク処理
      const size = 256
      const canvas = new OffscreenCanvas(size, size)
      const ctx = canvas.getContext('2d')

      // ポリゴン座標をタイルピクセル座標に変換してクリップパスを描画
      const coords = maskData.geometry.coordinates
      ctx.beginPath()
      for (let ring = 0; ring < coords.length; ring++) {
        const points = coords[ring]
        for (let j = 0; j < points.length; j++) {
          const lng = points[j][0]
          const lat = points[j][1]
          // 経緯度→タイル内ピクセル座標
          const px = (lngToTileX(lng, z) - x) * size
          const py = (latToTileY(lat, z) - y) * size
          if (j === 0) {
            ctx.moveTo(px, py)
          } else {
            ctx.lineTo(px, py)
          }
        }
        ctx.closePath()
      }
      ctx.clip()

      // クリップ済みの領域にタイル画像を描画
      ctx.drawImage(imgBitmap, 0, 0, size, size)

      // PNG出力
      const outputBlob = await canvas.convertToBlob({ type: 'image/png' })
      const data = new Uint8Array(await outputBlob.arrayBuffer())
      return { data }
    } catch (e) {
      return { data: new Uint8Array(0) }
    }
  })
}
